/**
 * DownloadProgress - Elegant loading animation for PNG downloads
 * Minimal, on-brand, professional
 */

import React, { useEffect, useState } from 'react';

interface DownloadProgressProps {
  isVisible: boolean;
  progress: number;
  message?: string;
}

export function DownloadProgress({ isVisible, progress, message = 'Preparing download...' }: DownloadProgressProps) {
  const [shouldRender, setShouldRender] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setShouldRender(true);
      // Small delay for smooth appear animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimateIn(true);
        });
      });
    } else {
      setAnimateIn(false);
      // Wait for fade out animation before removing from DOM
      const timer = setTimeout(() => {
        setShouldRender(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!shouldRender) return null;

  return (
    <div
      className={`
        fixed z-[9999] flex items-center justify-center
        transition-all duration-300 ease-out
        ${animateIn ? 'bg-black/20 backdrop-blur-sm' : 'bg-black/0 backdrop-blur-none'}
      `}
      style={{ 
        pointerEvents: isVisible ? 'auto' : 'none',
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100vw',
        height: '100vh'
      }}
    >
      <div
        className={`
          bg-white rounded-2xl shadow-2xl p-8 min-w-[320px] max-w-[400px]
          transition-all duration-300 ease-out
          ${animateIn ? 'scale-100 opacity-100' : 'scale-95 opacity-0'}
        `}
        style={{
          transform: animateIn ? 'none' : 'translateY(16px)'
        }}
      >
        {/* Logo/Brand Mark */}
        <div className="flex justify-center mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#294BFD] to-[#FF34A7] p-0.5">
            <div className="w-full h-full bg-white rounded-[10px] flex items-center justify-center">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 12L12 7L17 12M12 7V17"
                  stroke="url(#download-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 17H19"
                  stroke="url(#download-gradient)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient id="download-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#294BFD" />
                    <stop offset="100%" stopColor="#FF34A7" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </div>
        </div>

        {/* Message */}
        <div className="text-center mb-6">
          <p className="text-gray-900 font-semibold text-sm">
            {message}
          </p>
        </div>

        {/* Progress Bar Container */}
        <div className="relative">
          {/* Background Track */}
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            {/* Progress Fill */}
            <div
              className="h-full bg-gradient-to-r from-[#294BFD] to-[#FF34A7] rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Shimmer Effect */}
          <div
            className={`
              absolute inset-0 h-2 rounded-full overflow-hidden
              ${progress < 100 ? 'animate-shimmer' : ''}
            `}
          >
            <div
              className="h-full w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent"
              style={{ transform: 'translateX(-100%)' }}
            />
          </div>
        </div>

        {/* Progress Steps (subtle) */}
        <div className="flex justify-between mt-4 px-1">
          <div className={`transition-all duration-300 ${progress >= 33 ? 'opacity-100' : 'opacity-30'}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#294BFD" strokeWidth="1.5" />
              {progress >= 33 && (
                <path
                  d="M5 8L7 10L11 6"
                  stroke="#294BFD"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-check"
                />
              )}
            </svg>
          </div>
          <div className={`transition-all duration-300 ${progress >= 66 ? 'opacity-100' : 'opacity-30'}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#294BFD" strokeWidth="1.5" />
              {progress >= 66 && (
                <path
                  d="M5 8L7 10L11 6"
                  stroke="#294BFD"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-check"
                />
              )}
            </svg>
          </div>
          <div className={`transition-all duration-300 ${progress >= 100 ? 'opacity-100' : 'opacity-30'}`}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="7" stroke="#294BFD" strokeWidth="1.5" />
              {progress >= 100 && (
                <path
                  d="M5 8L7 10L11 6"
                  stroke="#294BFD"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="animate-check"
                />
              )}
            </svg>
          </div>
        </div>

        {/* Step Labels */}
        <div className="flex justify-between mt-2 px-1 text-[10px] text-gray-500">
          <span className={progress >= 33 ? 'text-gray-700' : ''}>Fonts</span>
          <span className={progress >= 66 ? 'text-gray-700' : ''}>Assets</span>
          <span className={progress >= 100 ? 'text-gray-700' : ''}>Ready</span>
        </div>
      </div>

      <style jsx>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(300%);
          }
        }

        @keyframes check {
          0% {
            stroke-dasharray: 20;
            stroke-dashoffset: 20;
          }
          100% {
            stroke-dasharray: 20;
            stroke-dashoffset: 0;
          }
        }

        .animate-shimmer > div {
          animation: shimmer 2s infinite;
        }

        .animate-check {
          animation: check 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
}